import { z } from 'zod';
import { jewelSocketProfileSchema } from '../inventory/jewel-profile';
import { PVP_HEALTH_MILLI_MAX_DIGITS } from './health';

/**
 * What a duelling hull IS, as four integers and one decimal string, on the wire.
 *
 * ## Why the shape lives in `shared` and the values do not
 *
 * The two ends need the same answer to "is this a legal profile", and only one
 * of them is the domain. `@kenocyber/game-domain` owns the DEFAULT - it is
 * derived from `pvp/balance.ts`, and duplicating those constants here would put
 * the duel's damage in two files. What has to be here is the SCHEMA and the
 * bounds it validates against, because a browser parses a welcome frame and a
 * browser must not import a simulation to do it.
 *
 * ## Representation bounds, not balance
 *
 * {@link PVP_PROFILE_LIMITS} is deliberately enormous next to anything the
 * jewel budget can reach. They exist so a malformed frame refuses by name
 * instead of producing a match with a hull nothing can kill or a gun that never
 * fires.
 *
 * They are emphatically NOT the default values. Validating a hull's ceiling
 * against `PVP_MAX_HEALTH` would refuse a legitimate Coração do Titã, and
 * capping a jewel's grant is the one thing `CLAUDE.md` forbids outright.
 *
 * Desde o gate V28G.S.1 a vida não está nesta tabela de todo: ela é
 * `maxHealthMilli`, uma string decimal sem tecto de magnitude, porque a vida
 * máxima é a da CONTA e a progressão é ilimitada. Ver `./health`.
 *
 * ## What a client may do with this
 *
 * Read it. Nothing more. There is no client frame that carries a profile, no
 * command field that mentions one, and no route that accepts one: the room
 * resolves every hull and states it. A profile arriving from a browser would be
 * a player choosing their own damage.
 */

/**
 * The widest a profile may be REPRESENTED, per field.
 *
 * Moved here from `game-domain` when the profile started crossing a wire: the
 * decoder that enforces them now runs in a browser, and a browser cannot import
 * the duel simulation to learn what a legal number is.
 */
export const PVP_PROFILE_LIMITS = Object.freeze({
  maxBaseDamage: 1_000_000,
  maxSpeedBps: 1_000_000,
  maxFireIntervalMilliTicks: 10_000_000,
});

/*
 * **`maxMaxHealth` foi REMOVIDO desta tabela** (gate V28G.S.1).
 *
 * A vida máxima deixou de ser um `number` de pontos inteiros e passou a ser
 * milli numa string decimal, portanto não tem um tecto de REPRESENTAÇÃO para
 * declarar: um `bigint` representa qualquer magnitude, e o que a protege de um
 * quadro hostil é um comprimento de payload - `PVP_HEALTH_MILLI_MAX_DIGITS`, em
 * `./health`.
 *
 * Deixá-lo aqui convidaria alguém a validar a vida contra ele, que é como um
 * teto de progressão entra num esquema sem ninguém decidir que entrou.
 */

/**
 * O comprimento máximo da contagem de corações NO FIO, em dígitos.
 *
 * Um limite de PAYLOAD e não de jogo, e a diferença importa: 80 é o dobro da
 * capacidade de armazenamento (`numeric(40, 0)`), portanto nenhuma contagem real
 * chega perto dele. Existe para que um quadro hostil não entregue ao parser um
 * inteiro de um megabyte, não para dizer quantos corações alguém pode ter.
 *
 * Deliberadamente FORA de `PVP_PROFILE_LIMITS`: aquilo é a tabela dos tectos de
 * representação de um casco, e pôr este lá dentro convidaria alguém a validá-lo
 * como se fosse um deles - o que seria capar um item.
 */
export const PVP_PROFILE_HEART_COUNT_MAX_DIGITS = 80;

/**
 * One hull's four numbers, as a frame carries them.
 *
 * `.strict()`, so nothing rides along beside them - no account, no item, no
 * socket, no jewel id and no coefficient. A profile is the RESULT of whatever
 * the server resolved; what produced it is the server's business and stays
 * there.
 *
 * Os quatro primeiros são inteiros positivos. Zero é recusado nos quatro, e cada
 * recusa é uma partida impossível diferente: sem dano é uma arma que não mata,
 * sem vida é um casco já morto, sem velocidade é uma nave pregada ao spawn, e
 * sem intervalo é uma salva infinita num tique.
 *
 * O quinto é uma string decimal, e nele zero é LEGAL - um casco sem Corações
 * Primordiais é o casco normal, e é o que quase todos os duelos usam.
 */
export const pvpCombatProfileSchema = z
  .object({
    /** Whole damage of one ordinary projectile, before any rune. */
    baseDamage: z.number().int().min(1).max(PVP_PROFILE_LIMITS.maxBaseDamage),
    /**
     * **O teto deste casco, em MILLI, como STRING DECIMAL** (gate V28G.S.1).
     *
     * O denominador contra o qual a barra é desenhada, e a vida máxima
     * autoritativa da conta de quem se sentou - a MESMA que a caça usa, com a
     * fração incluída. Era `maxHealth`, um `number` de pontos inteiros, e isso
     * truncava até 0,999 de vida que a caça tinha: nenhuma fonte legal produz
     * pontos inteiros (ver `./health`).
     *
     * String e não número, pela mesma razão que `primordialHeartCount`: a
     * progressão não tem tecto e 2^40 pontos passam de `Number.MAX_SAFE_INTEGER`
     * em milli. Um mínimo de um milésimo, porque um casco sem vida nenhuma já
     * está morto.
     */
    maxHealthMilli: z
      .string()
      .min(1)
      .max(PVP_HEALTH_MILLI_MAX_DIGITS)
      .regex(/^[1-9][0-9]*$/, 'must be a canonical positive decimal'),
    /** Movement speed in integer basis points of the base. 10 000 is unmodified. */
    speedBps: z.number().int().min(1).max(PVP_PROFILE_LIMITS.maxSpeedBps),
    /**
     * Ticks between volleys, in MILLI-ticks: 1000 is one tick.
     *
     * A whole tick cannot express a percentage - the shipped interval is 4, and
     * +9 % asks for 3,67 - so the client predicts cadence in the same unit the
     * room accumulates it in, or the two disagree by a third.
     */
    fireIntervalMilliTicks: z
      .number()
      .int()
      .min(1)
      .max(PVP_PROFILE_LIMITS.maxFireIntervalMilliTicks),
    /**
     * Corações Primordiais incorporados na nave equipada, como STRING DECIMAL.
     *
     * ## Porque uma string, e porque nenhum tecto
     *
     * Porque é o único campo do perfil que o produto decidiu não limitar. Os
     * outros quatro são propriedades do casco com um tecto de REPRESENTAÇÃO;
     * este é uma contagem guardada em `numeric(40, 0)`, e um `number` de JSON
     * perderia dígitos acima de 2^53 - um teto de item escrito num tipo.
     *
     * `PVP_PROFILE_LIMITS` não ganhou uma entrada para ele de propósito: um
     * máximo aqui recusaria um casco legítimo, que é uma forma de apagar o que
     * um jogador pagou. O que o esquema garante é a FORMA - decimal canónico,
     * não negativo, sem zeros à esquerda - e um comprimento de payload.
     *
     * ## O cliente lê, e nunca envia
     *
     * Como os outros quatro. Não há quadro de cliente que transporte um perfil e
     * não há rota que o aceite: a sala resolve cada casco e declara-o. Um perfil
     * vindo de um browser seria um jogador a escolher o seu próprio dano.
     */
    primordialHeartCount: z
      .string()
      .min(1)
      .max(PVP_PROFILE_HEART_COUNT_MAX_DIGITS)
      .regex(/^(0|[1-9][0-9]*)$/, 'must be a canonical non-negative decimal'),
    /**
     * As joias de socket deste casco, agregadas (gate V28G.A).
     *
     * ## Porque o esquema do perfil é reutilizado e não repetido
     *
     * `jewelSocketProfileSchema` já é a definição do que é legal: as catorze
     * identidades derivadas do catálogo, o tecto de seis assentos, a coerência
     * entre contagem e soma, a ordem canónica e nenhuma chave desconhecida.
     * Uma segunda cópia aqui seria um segundo sítio onde essa pergunta pode ser
     * respondida, e a primeira a divergir seria a que ninguém tocou.
     *
     * ## `.default` e o que a ausência significa
     *
     * Um quadro emitido por uma build anterior a este gate não tem o campo, e a
     * ausência tem um significado EXATO - zero joias - porque nenhum socket
     * podia conter alguma coisa quando esse quadro foi escrito. O `default` é
     * essa tradução escrita, não um palpite.
     *
     * ## O cliente lê, e nunca envia
     *
     * Como os outros cinco. A sala resolve cada casco e declara-o; um perfil
     * vindo de um browser seria um jogador a escolher as suas próprias joias.
     */
    jewels: jewelSocketProfileSchema.default(() => ({ entries: [] })),
  })
  .strict();

export type PvpCombatProfileWire = z.infer<typeof pvpCombatProfileSchema>;

/**
 * A profile rebuilt field by field, sharing nothing with what it came from.
 *
 * Six clients read one world per tick, and a profile handed out by reference
 * would be one object six frames could see each other's edits through - the
 * same class of mistake `team-wire.ts` documents for its arrays.
 */
export function pvpCopyCombatProfileWire(profile: PvpCombatProfileWire): PvpCombatProfileWire {
  return {
    baseDamage: profile.baseDamage,
    maxHealthMilli: profile.maxHealthMilli,
    speedBps: profile.speedBps,
    fireIntervalMilliTicks: profile.fireIntervalMilliTicks,
    primordialHeartCount: profile.primordialHeartCount,
    /*
     * Reconstruído entrada a entrada, pela mesma razão que os quatro números
     * são copiados: seis clientes leem um mundo por tique, e um array entregue
     * por referência seria um objeto através do qual seis quadros veem as
     * edições uns dos outros.
     */
    jewels: { entries: profile.jewels.entries.map((entry) => ({ ...entry })) },
  };
}
